import { useQuery } from "@tanstack/react-query";
import { ApiException } from "../../entities";
import { fetchEnvironment, fetchMailboxGroup } from "../../api";
import { useParams } from "react-router-dom";
import { AxiosError } from "axios";
import NotFoundPage from "../NotFound";
import { isApiException } from "../../utils/typeGuard";
import { ApiExceptionErrorPage, ErrorPage } from "../error";
import { EnvironmentProvider } from "../../providers/environment";
import MailboxPageSkeleton from "../skeletons/Mailbox";
import { LoadingScreen } from "./loadingScreen";
import { PageContent } from "./content";

export const MailboxGroupPage = () => {
  const { key } = useParams();

  if (key === undefined) {
    throw new Error(
      "Either mailbox group's vanity link or id need to be defined"
    );
  }

  const { status, data, error } = useQuery({
    queryKey: ["mailboxGroupAndEnvironment", key],
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () =>
      Promise.all([fetchMailboxGroup(key), fetchEnvironment()]),
  });

  switch (status) {
    case "loading":
      return (
        <MailboxPageSkeleton
          childrenVerticalAlignment="start"
          hasMailboxLoaded={false}
        >
          <div className="h-3 sm:h-16" />
          <div className="place-self-center">
            <LoadingScreen />
          </div>
          <div className="h-[90px] w-full" />
        </MailboxPageSkeleton>
      );
    case "error":
      if (error instanceof AxiosError && error.response) {
        if (error.response?.status === 404) {
          return <NotFoundPage />;
        }

        const isErrorApiException: boolean = isApiException(
          error.response.data
        );

        if (isErrorApiException) {
          const apiException: ApiException = error.response
            .data as ApiException;

          const errorMessage: string = apiException.error.details.reduce(
            (acc, curr) => acc + " " + curr,
            ""
          );

          return <ApiExceptionErrorPage errorMessage={errorMessage} />;
        }
      }

      return <ErrorPage errorMessage={(error as Error).message} />;
    case "success":
      const [mailboxGroup, env] = data;

      return (
        <EnvironmentProvider environment={env}>
          <MailboxPageSkeleton
            childrenVerticalAlignment="start"
            hasMailboxLoaded={true}
          >
            <div className="animate-fade-in">
              <div className="h-3 sm:h-16" />
              <div className="w-full place-self-center">
                <PageContent
                  groupDescription={mailboxGroup.description}
                  mailboxes={mailboxGroup.mailboxes}
                />
              </div>
            </div>
          </MailboxPageSkeleton>
        </EnvironmentProvider>
      );
  }
};
